import { useEffect, useState } from 'react';
import { Copy, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * iter879 — Duplicate Menu to Another Branch wizard.
 *
 * Copies every menu item currently sold at the source branch into the
 * destination branch, applying a caller-supplied FX rate. Dry-run first
 * gives a preview; the live run mutates branch_ids + branch_prices per item.
 */
const SUGGESTED_RATES = {
  // Rough long-term reference rates (not live). Users can override.
  'SAR-AED': 0.98,
  'AED-SAR': 1.02,
  'SAR-INR': 22.2,
  'INR-SAR': 0.045,
  'AED-INR': 22.7,
  'INR-AED': 0.044,
  'USD-SAR': 3.75,
  'SAR-USD': 0.267,
  'USD-AED': 3.67,
  'AED-USD': 0.272,
  'GBP-SAR': 4.79,
  'SAR-GBP': 0.209,
};

export function DuplicateMenuDialog({ open, onOpenChange, branches, onDone }) {
  const [src, setSrc] = useState('');
  const [dst, setDst] = useState('');
  const [rate, setRate] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const srcBranch = branches.find(b => b.id === src);
  const dstBranch = branches.find(b => b.id === dst);
  const suggested = srcBranch && dstBranch
    ? SUGGESTED_RATES[`${srcBranch.currency_code || 'SAR'}-${dstBranch.currency_code || 'SAR'}`]
    : null;

  useEffect(() => {
    if (open) { setSrc(''); setDst(''); setRate(''); setPreview(null); }
  }, [open]);

  const runPreview = async () => {
    if (!src || !dst || !rate) return;
    setLoading(true);
    try {
      const { data } = await api.post('/cashier/menu/duplicate-to-branch', {
        source_branch_id: src, dest_branch_id: dst,
        exchange_rate: parseFloat(rate), dry_run: true,
      });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setLoading(false); }
  };

  const runLive = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/cashier/menu/duplicate-to-branch', {
        source_branch_id: src, dest_branch_id: dst,
        exchange_rate: parseFloat(rate), dry_run: false,
      });
      toast.success(`${data.updated} items copied to ${dstBranch?.name} at rate ${data.exchange_rate}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="duplicate-menu-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-outfit"><Copy size={18} /> Duplicate Menu to Another Branch</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Source */}
          <div>
            <Label className="text-xs">Copy from</Label>
            <Select value={src} onValueChange={setSrc}>
              <SelectTrigger data-testid="dup-src-select"><SelectValue placeholder="Choose source branch..." /></SelectTrigger>
              <SelectContent>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.currency_code || '—'})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Arrow + Destination */}
          <div className="flex items-center justify-center text-muted-foreground -my-1"><ArrowRight size={16} /></div>
          <div>
            <Label className="text-xs">Copy to</Label>
            <Select value={dst} onValueChange={setDst}>
              <SelectTrigger data-testid="dup-dst-select"><SelectValue placeholder="Choose destination branch..." /></SelectTrigger>
              <SelectContent>
                {branches.filter(b => b.id !== src).map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.currency_code || '—'})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Rate */}
          {src && dst && (
            <div>
              <Label className="text-xs flex items-center gap-2">
                Exchange rate — 1 {srcBranch?.currency_code || 'src'} = <span className="font-mono">?</span> {dstBranch?.currency_code || 'dest'}
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.001"
                  value={rate}
                  onChange={e => { setRate(e.target.value); setPreview(null); }}
                  placeholder={suggested ? `Suggested: ${suggested}` : 'e.g. 0.98'}
                  data-testid="dup-rate-input"
                />
                {suggested && (
                  <Button type="button" variant="outline" size="sm" onClick={() => { setRate(String(suggested)); setPreview(null); }} data-testid="dup-use-suggested-btn">
                    Use {suggested}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Approximate rates only — verify with your accountant before publishing.</p>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/40 rounded-lg space-y-2" data-testid="dup-preview">
              <p className="text-xs font-semibold text-orange-800 dark:text-orange-300">
                Will copy {preview.matched} item{preview.matched === 1 ? '' : 's'} — {preview.source_currency} → {preview.dest_currency} @ {preview.exchange_rate}
              </p>
              <div className="text-[11px] font-mono space-y-0.5 max-h-32 overflow-y-auto">
                {preview.sample.map(s => (
                  <div key={s.id} className="flex justify-between">
                    <span className="truncate">{s.name}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {s.source_price} {preview.source_currency} → <span className="text-orange-700 dark:text-orange-400 font-bold">{s.dest_price} {preview.dest_currency}</span>
                    </span>
                  </div>
                ))}
                {preview.matched > preview.sample.length && (
                  <div className="text-[10px] text-muted-foreground pt-1">… and {preview.matched - preview.sample.length} more</div>
                )}
              </div>
              <div className="flex items-start gap-1.5 text-[10px] text-orange-700 dark:text-orange-400">
                <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                Existing overrides at destination will be overwritten. Non-price fields (name, modifiers, images) are shared across branches.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          {!preview ? (
            <Button
              onClick={runPreview}
              disabled={!src || !dst || !rate || loading}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="dup-preview-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Preview
            </Button>
          ) : (
            <Button
              onClick={runLive}
              disabled={loading}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="dup-confirm-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Confirm — copy {preview.matched} items
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DuplicateMenuDialog;
